// =============================================================================
// DEAL GIFT CARD - Uses centralized registry and styles (CR043)
// CR098: Single canonical deal card component used across all views
// CR098: Task 2 - Consumes DB config via DealCardConfigProvider context
// CR099: Semantic merchant/brand separation with key-based comparison
// CR106: Consumes global theme tokens from GlobalThemeProvider
// CR130: Uses DealCardUXContext for cardSize/density standardization
// CR-MerchantName: Uses MerchantNameBlock with placement tokens
// =============================================================================

import { useState, useEffect, useMemo } from "react";
import { AlertTriangle, Building2, ChevronDown, ExternalLink, FileText, Gift, HelpCircle, Info, Lock, ShieldAlert, ShieldCheck, Shield, UserCheck } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { 
  type Deal, 
  type DealCategory,
  calculateEffectiveValue, 
  getDealSummary, 
  formatValidity, 
  formatBonusValidity,
  formatWhereFound,
  formatDiscountTiers,
  hasMissingDetails,
  hasMissingDates,
  isSameMerchantBonus,
  isBulkDeal,
  isRangePercentDeal,
  isBonusPercentage,
  getBonusPercentageValue,
  getHeadlineMechanic,
  hasValuation,
  needsConfirmOnSite,
  computeDealValueCad,
  computeEffectivePercentBack,
  getDiscountDisplayStrings,
  isBonusPercentBackDeal,
} from "@/lib/types/deals";
import { findMerchantByName, CATEGORY_LABELS } from "@/lib/config/merchants";
import { cn } from "@/lib/utils";
import { resolveMerchantLogo, resolveBrandLogo, type LogoResult } from "@/lib/merchantLogoResolver";

// CR098: Import unified getDealType helper for consistent type resolution
import { getDealType, logUnclassifiedDeal } from "@/lib/deals/getDealType";

// CR098: Import config provider hook for DB-backed config
import { useDealCardConfig } from "@/providers/DealCardConfigProvider";

// CR106: Import global theme provider for design tokens
import { useGlobalTheme } from "@/providers/GlobalThemeProvider";

// CR130: Import card UX context for size/density
import { 
  useDealCardUX, 
  getCardSizeClasses, 
  getDensityClasses, 
  getTitleClampClass, 
  getHeadlineClampClass,
  getHeaderHeightClass,
  getBodyFlexClass,
} from "@/contexts/DealCardUXContext";

// CR098: Import loading skeleton and debug strip
import { DealCardLoadingSkeleton } from "./DealCardLoadingSkeleton";
import { DealCardConfigDebugStrip } from "./DealCardConfigDebugStrip";

// CR099: Import key-based merchant comparison utilities
import { shouldShowRewardChip, toMerchantKey } from "@/lib/deals/merchantKeyUtils";

// CR-MerchantName: Import merchant name block component and types
import { MerchantNameBlock } from "./MerchantNameBlock";
import type { MerchantNamePlacement, MerchantNameStyle } from "@/lib/deals/globalThemeTokens";

// CR225: Single source of truth for visibility
import { getEffectiveVisibility, type VisibilityInputs } from "@/lib/deals/effectiveVisibility";

// CR229: Points parsing fallback for deals without normalized points data
import { getEffectivePointsValues } from "@/lib/deals/pointsParser";

// CR304: Centralized display helpers for title/condition
import { getDealCardTitle, getDealCardCondition, isProbablyBadBrand, stripNumericPrefix } from "@/lib/deals/dealCardDisplay";

// CR229: Section ordering support
import { DEFAULT_SECTION_ORDER } from "@/components/admin/command-center/SectionOrderPanel";

// CR305: Debug render signature (safe iframe detection)
import { 
  shouldRenderDealCardSignatureStrip, 
  buildDealCardSignature 
} from "@/lib/deals/debugDealCardSignature";

import { ValueStarburstBadge } from "./ValueStarburstBadge";
import { DealTypeBadge } from "./DealTypeBadge";
import { DealDetailLayout } from "./DealDetailLayouts";
import { DealStatusBadge, computeDealStatus, type DealStatus } from "./DealStatusBadge";
import { AvailabilityScopeBadge } from "./AvailabilityScopeBadge";

// CR098: Extended props to support preview overrides
interface DealGiftCardProps {
  deal: Deal & { id?: string; dedupe_key?: string };
  isAdmin?: boolean;
  /** CR098: Optional registry override for live preview (bypasses context) */
  registryOverride?: {
    ribbon_text: string;
    ribbon_theme: string;
    is_enabled: boolean;
    label?: string;
  } | null;
  /** CR098: Optional template override for live preview (bypasses context) */
  templateOverride?: {
    sections?: Record<string, boolean>;
    labels?: Record<string, string>;
    header?: Record<string, unknown>;
    extended_sections?: Record<string, boolean>;
    styling?: Record<string, unknown>;
    section_order?: string[];
    theme_preset?: string;
  } | null;
}

// CR105: Normalize confidence to a display label
// Handles both string ("High"/"Medium"/"Low") and numeric (0-1) values
function normalizeConfidence(confidence: Deal["confidence"]): "High" | "Medium" | "Low" {
  if (typeof confidence === "string") {
    if (confidence === "High" || confidence === "Medium" || confidence === "Low") {
      return confidence;
    }
    // Unknown string - default to Low
    return "Low";
  }
  if (typeof confidence === "number") {
    if (confidence >= 0.8) return "High";
    if (confidence >= 0.5) return "Medium";
    return "Low";
  }
  // Null/undefined/other - default to Low
  return "Low";
}

// Get confidence icon and color
function getConfidenceDisplay(confidence: Deal["confidence"]) {
  const normalized = normalizeConfidence(confidence);
  switch (normalized) {
    case "High":
      return {
        icon: ShieldCheck,
        color: "text-success",
        bgColor: "bg-success/10",
        label: "High",
        normalized,
      };
    case "Medium":
      return {
        icon: Shield,
        color: "text-accent",
        bgColor: "bg-accent/10",
        label: "Medium",
        normalized,
      };
    case "Low":
      return {
        icon: ShieldAlert,
        color: "text-muted-foreground",
        bgColor: "bg-muted",
        label: "Low",
        normalized,
      };
  }
}

export function DealGiftCard({ 
  deal, 
  isAdmin = false,
  registryOverride,
  templateOverride,
}: DealGiftCardProps) {
  const [logoError, setLogoError] = useState(false);
  const [merchantLogoError, setMerchantLogoError] = useState(false);
  
  // CR098: Get config from context (no per-card fetching)
  const { 
    getRegistryForType, 
    getTemplateForType, 
    getRibbonClass,
    isKnownType,
    configLoaded,
    loading: configLoading,
    lastUpdatedAt,
  } = useDealCardConfig();
  
  // CR106: Get global theme tokens for design system defaults
  const { styles: globalStyles, preset: globalPreset } = useGlobalTheme();
  
  // CR130: Get card UX config (size/density) from context
  // CR171: Now includes customHeightsEnabled and customHeightsPx for dynamic heights
  const { 
    cardSize, 
    density, 
    cardHeightPx, 
    spacing: densitySpacing,
    customHeightsEnabled,
    customHeightsPx,
  } = useDealCardUX();
  
  // CR171: Use inline styles for height when custom heights are enabled, otherwise use Tailwind classes
  const cardSizeClasses = customHeightsEnabled ? '' : getCardSizeClasses(cardSize);
  const cardHeightStyle = customHeightsEnabled ? { height: `${cardHeightPx}px` } : undefined;
  
  const densityClasses = getDensityClasses(density);
  const titleClamp = getTitleClampClass(cardSize);
  const headlineClamp = getHeadlineClampClass(cardSize);
  const headerHeightClass = getHeaderHeightClass(cardSize);
  const bodyFlexClass = getBodyFlexClass();
  
  // CR098: Use unified getDealType helper for consistent category resolution
  const { category: rawDealCategory, debug: dealTypeDebug } = getDealType(deal);
  
  // CR098 Task 3: Runtime enforcement - unknown types → UNCLASSIFIED with warning
  const originalDealType = deal.deal_type || deal.deal_category || null;
  const isUnknownType = !isKnownType(rawDealCategory);
  const dealCategory = isUnknownType ? "UNCLASSIFIED" : rawDealCategory;
  
  // CR098: Get DB-backed registry and template for the resolved type
  // If overrides are provided (preview mode), use them instead
  const baseRegistry = getRegistryForType(dealCategory);
  const baseTemplate = getTemplateForType(dealCategory);
  
  // CR098: Apply overrides for live preview - merge with base to ensure all fields exist
  const registry = registryOverride ? {
    ...baseRegistry,
    ribbon_text: registryOverride.ribbon_text,
    ribbon_theme: registryOverride.ribbon_theme,
    is_enabled: registryOverride.is_enabled,
    label: registryOverride.label || baseRegistry?.label || dealCategory,
  } : baseRegistry;
  
  const template = templateOverride ? {
    sections: {
      ...baseTemplate.sections,
      ...(templateOverride.sections || {}),
      ...(templateOverride.extended_sections || {}),
    },
    labels: {
      ...baseTemplate.labels,
      ...(templateOverride.labels || {}),
    },
  } : baseTemplate;
  
  // CR100: Extract styling from template override for live preview
  // This uses the CardStyleConfig contract for consistent key names
  const styling = templateOverride?.styling as {
    // Container
    cornerRadius?: number;
    backgroundType?: "solid" | "gradient";
    backgroundColor?: string;
    gradientFrom?: string;
    gradientTo?: string;
    gradientDirection?: "to-r" | "to-br" | "to-b" | "to-bl";
    borderWidth?: number;
    borderColor?: string;
    shadowIntensity?: "none" | "low" | "medium" | "high";
    // Header
    headerHeight?: "sm" | "md" | "lg";
    headerBackgroundType?: "solid" | "gradient";
    headerBackgroundColor?: string;
    headerGradientFrom?: string;
    headerGradientTo?: string;
    showMerchantLogo?: boolean;
    showMerchantName?: boolean;
    // Decorations - CR100 FIX: These control ribbon/starburst visibility
    showRibbon?: boolean;
    ribbonPosition?: "top-right" | "top-left";
    ribbonOffsetX?: number;
    ribbonOffsetY?: number;
    showStarburst?: boolean;
    starburstPosition?: "top-left" | "top-right";
    starburstOffsetX?: number;
    starburstOffsetY?: number;
  } | undefined;
  
  const themePreset = templateOverride?.theme_preset as "light" | "dark" | "custom" | undefined;
  
  // CR229: Extract section order from template override
  // When provided, this controls the order in which body sections are rendered
  const sectionOrder = templateOverride?.section_order ?? DEFAULT_SECTION_ORDER;
  
  // CR100/CR105/CR225: Debug log in admin mode to verify styling is being passed
  if (isAdmin && templateOverride) {
    console.log("[DealGiftCard styleOverride]", {
      styling,
      showRibbon: styling?.showRibbon,
      showStarburst: styling?.showStarburst,
      showMerchantLogo: styling?.showMerchantLogo,
      showMerchantName: styling?.showMerchantName, // CR225: Now the canonical source
      cornerRadius: styling?.cornerRadius,
      backgroundType: styling?.backgroundType,
      themePreset,
      sectionOrder, // CR229: Log section order
    });
  }
  
  // CR225: SINGLE SOURCE OF TRUTH for all visibility decisions
  // All render paths read from this computed object - NO direct reads from styling.showX or template.sections.X
  const { tokens: globalTokens } = useGlobalTheme();
  
  const visibilityInputs: VisibilityInputs = useMemo(() => ({
    styling: {
      showMerchantLogo: styling?.showMerchantLogo,
      showMerchantName: styling?.showMerchantName,
      showRibbon: styling?.showRibbon,
      showStarburst: styling?.showStarburst,
    },
    sections: template.sections as VisibilityInputs["sections"],
    globalTokens: {
      components: {
        merchantName: {
          placement: globalTokens?.components?.merchantName?.placement,
          style: globalTokens?.components?.merchantName?.style,
        },
      },
    },
    surface: isAdmin ? "admin" : "public",
    isAdmin,
  }), [styling, template.sections, globalTokens, isAdmin]);
  
  const effectiveVisibility = useMemo(
    () => getEffectiveVisibility(visibilityInputs),
    [visibilityInputs]
  );
  
  // CR225: Destructure for cleaner render code - these are the ONLY visibility flags used below
  const {
    showRibbon: shouldShowRibbon,
    showStarburst: shouldShowStarburst,
    showMerchantLogo: shouldShowMerchantLogo,
    showMerchantName: shouldShowMerchantName,
    showMerchantInHeader,
    showMerchantBodyTop,
    showMerchantBelowChips,
    showMerchantAboveDetails,
    showMerchantFooter,
    showMerchantInChipsTitle,
    showHeadline,
    showDates,
    showPromoBadges,
    showDetailsPills,
    showDebugStrip,
    merchantNamePlacement,
    merchantNameStyle,
  } = effectiveVisibility;
  
  // CR225 Debug: Log effective visibility in admin mode
  if (isAdmin && templateOverride) {
    console.log("[DealGiftCard CR225] effectiveVisibility", {
      inputs: visibilityInputs,
      computed: effectiveVisibility,
    });
  }
  
  // CR106: Generate dynamic container styles from styling override OR global tokens
  // Priority: templateOverride styling > global theme tokens > hardcoded fallbacks
  const containerRadius = styling?.cornerRadius 
    ? `${styling.cornerRadius}px` 
    : globalStyles.cardCornerRadius;
  
  // CR106: Background - use global tokens as fallback
  const containerBgColor = useMemo(() => {
    if (styling?.backgroundType === "gradient" && styling.gradientFrom && styling.gradientTo) {
      const directionMap: Record<string, string> = {
        "to-r": "to right",
        "to-br": "to bottom right",
        "to-b": "to bottom",
        "to-bl": "to bottom left",
      };
      const direction = directionMap[styling.gradientDirection || "to-b"] || "to bottom";
      const from = styling.gradientFrom.includes("hsl") ? styling.gradientFrom : `hsl(${styling.gradientFrom})`;
      const to = styling.gradientTo.includes("hsl") ? styling.gradientTo : `hsl(${styling.gradientTo})`;
      return `linear-gradient(${direction}, ${from}, ${to})`;
    }
    if (styling?.backgroundColor) {
      return styling.backgroundColor.includes("hsl") ? styling.backgroundColor : `hsl(${styling.backgroundColor})`;
    }
    // CR106: Fallback to global theme tokens
    return globalStyles.cardBackground;
  }, [styling, globalStyles.cardBackground]);
  
  // CR106: Border uses global tokens as fallback
  const containerBorderWidth = styling?.borderWidth ?? (parseInt(globalStyles.cardBorderWidth) || 1);
  const containerBorderColor = useMemo(() => {
    if (styling?.borderColor) {
      return styling.borderColor.includes("hsl") || styling.borderColor.includes("rgba") 
        ? styling.borderColor 
        : `hsl(${styling.borderColor})`;
    }
    // CR106: Fallback to global theme tokens
    return globalStyles.cardBorder;
  }, [styling?.borderColor, globalStyles.cardBorder]);
  
  // CR106: Shadow and header height use global tokens
  const shadowIntensity = styling?.shadowIntensity || "medium";
  const headerHeight = styling?.headerHeight || "md";
  
  // CR106: Header gradient colors - use global tokens as fallback
  const headerGradientFrom = useMemo(() => {
    if (styling?.headerGradientFrom) {
      return styling.headerGradientFrom.includes("hsl") || styling.headerGradientFrom.includes("rgba")
        ? styling.headerGradientFrom
        : `hsl(${styling.headerGradientFrom})`;
    }
    // CR106: Use global header background as fallback
    return globalStyles.headerBackground;
  }, [styling?.headerGradientFrom, globalStyles.headerBackground]);
  
  const headerGradientTo = useMemo(() => {
    if (styling?.headerGradientTo) {
      return styling.headerGradientTo.includes("hsl") || styling.headerGradientTo.includes("rgba")
        ? styling.headerGradientTo
        : `hsl(${styling.headerGradientTo})`;
    }
    // CR106: Slightly lighter version of header background
    return globalStyles.headerBackground;
  }, [styling?.headerGradientTo, globalStyles.headerBackground]);
  
  // CR106: Header text color from global tokens
  const headerTextColor = globalStyles.headerText;
  
  // CR106: Use global shadow from tokens
  const containerShadow = globalStyles.cardShadow;
  const containerHoverShadow = shadowIntensity === "none" ? "none" : 
    shadowIntensity === "low" ? "0 6px 16px rgba(0,0,0,0.06)" :
    shadowIntensity === "medium" ? "0 14px 38px rgba(0,0,0,0.10)" :
    "0 24px 60px rgba(0,0,0,0.18)";
  
  // CR106: Header height from global tokens
  const headerHeightPx = globalStyles.headerHeight;
  
  // CR500 Phase 2A: Static ribbon theme map - prevents dynamic Tailwind issues
  const RIBBON_THEME_CLASS: Record<string, string> = {
    emerald: "bg-emerald-600",
    purple: "bg-purple-600",
    amber: "bg-amber-600",
    slate: "bg-slate-600",
    blue: "bg-blue-600",
    green: "bg-green-600",
    orange: "bg-orange-500",
    gray: "bg-gray-500",
    red: "bg-red-600",
    teal: "bg-teal-600",
    navy: "bg-slate-800",
  };
  
  // CR098/CR500: Ribbon class - use static map instead of dynamic construction
  const ribbonClass = registryOverride 
    ? (RIBBON_THEME_CLASS[registryOverride.ribbon_theme] ?? getRibbonClass(dealCategory))
    : getRibbonClass(dealCategory);
  
  // CR-Corporate: Corporate badge uses eligibility_scope (preferred) or legacy audience_hint
  const isCorporateDeal = deal.eligibility_scope === "CORPORATE" || deal.audience_hint === "corporate_bulk";
  
  // Debug logging for corporate pill (admin mode only)
  if (isAdmin && (deal.eligibility_scope || deal.audience_hint)) {
    console.log(`[CR-Corporate] ${deal.merchant}: eligibility_scope=${deal.eligibility_scope}, audience_hint=${deal.audience_hint}, isCorporate=${isCorporateDeal}`);
  }
  
  // CR015: Unclassified deal badge
  const isUnclassifiedDeal = dealCategory === "UNCLASSIFIED";
  
  // CR014: Membership requirement badge
  const hasMembershipRequirement = deal.access_requirement_type && deal.access_requirement_type !== 'NONE';
  const membershipBadgeLabel = hasMembershipRequirement 
    ? deal.access_requirement_type === 'MEMBERSHIP_REQUIRED' ? 'Members only'
    : deal.access_requirement_type === 'LOGIN_REQUIRED' ? 'Login required'
    : deal.access_requirement_type === 'SIGNUP_REQUIRED' ? 'Sign-up required'
    : null
    : null;
  
  const effectiveValue = calculateEffectiveValue(deal);
  const summary = getDealSummary(deal);
  const validity = formatValidity(deal);
  const bonusValidity = formatBonusValidity(deal);
  const whereFound = formatWhereFound(deal);
  const confidenceDisplay = getConfidenceDisplay(deal.confidence);
  const ConfidenceIcon = confidenceDisplay.icon;
  const isMissingDetails = hasMissingDetails(deal);
  const isMissingDates = hasMissingDates(deal);
  
  // Derived properties
  const sameMerchantBonus = isSameMerchantBonus(deal);
  const bulkDeal = isBulkDeal(deal);
  const rangePercentDeal = isRangePercentDeal(deal);
  const bonusIsPercent = isBonusPercentage(deal);
  const bonusPercentValue = getBonusPercentageValue(deal);
  const discountTiers = formatDiscountTiers(deal);
  
  // CR098: Log UNCLASSIFIED deals in admin mode for debugging
  useEffect(() => {
    if (isAdmin && (dealCategory === "UNCLASSIFIED" || isUnknownType)) {
      const dealWithId = deal as Deal & { id?: string; dedupe_key?: string };
      logUnclassifiedDeal(dealWithId.id, dealWithId.dedupe_key, dealTypeDebug);
      
      if (isUnknownType) {
        console.warn(`[DealGiftCard] Unknown deal_type "${originalDealType}" → UNCLASSIFIED`, {
          deal_id: dealWithId.id,
          dedupe_key: dealWithId.dedupe_key,
        });
      }
    }
  }, [isAdmin, dealCategory, deal, dealTypeDebug, isUnknownType, originalDealType]);
  
  const headlineMechanic = getHeadlineMechanic(deal);
  const showConfirmOnSite = needsConfirmOnSite(deal);
  const dealHasValuation = hasValuation(deal);
  const computedValue = computeDealValueCad(deal);
  const percentBack = computeEffectivePercentBack(deal);
  
  // CR432: Use unified helper via deal object (timezone-safe, unknown-aware)
  const dealStatus: DealStatus = computeDealStatus(deal as any);
  
  // For discount deals, use specialized display strings
  const discountDisplay = dealCategory === "DISCOUNT" ? getDiscountDisplayStrings(deal) : null;
  
  // CR229 Spec: Generate improved headline for gift card bonus deals
  // Format: "Buy a $<spend> <brand> gift card at <location>, get a $<bonus> bonus gift card."
  const generateGiftCardBonusHeadline = (): string | null => {
    const gcBrand = deal.purchase_gift_card_brand ?? deal.gift_card_brand;
    const location = deal.promo_merchant_name || deal.merchant;
    const spend = deal.spend_amount ?? deal.qualifying_spend_cad;
    const bonus = deal.bonus_amount ?? deal.bonus_value_cad;
    
    if (!gcBrand || !location) return null;
    
    // Build headline parts
    const spendPart = spend ? `$${spend} ` : "";
    const bonusPart = bonus ? `$${bonus} ` : "";
    
    return `Buy a ${spendPart}${gcBrand} gift card at ${location}, get a ${bonusPart}bonus gift card.`;
  };
  
  // Determine headline: discount-specific > gift card bonus > general mechanic
  const giftCardBonusHeadline = dealCategory === "BONUS" && (deal.purchase_gift_card_brand || deal.gift_card_brand)
    ? generateGiftCardBonusHeadline()
    : null;
  const displayHeadline = discountDisplay?.headline || giftCardBonusHeadline || headlineMechanic;
  const headlineMode = discountDisplay?.headlineMode || "DEFAULT";
  
  // Try to find merchant in universe for category info
  const merchantConfig = findMerchantByName(deal.merchant);
  const categoryLabel = merchantConfig?.categoryKey 
    ? CATEGORY_LABELS[merchantConfig.categoryKey] 
    : null;
  
  // ==========================================================================
  // CR099: Semantic merchant/brand resolution (enhanced per 8-point review)
  // promo_merchant = where you spend/buy (e.g., Shoppers, Costco)
  // reward_brand = what you get (e.g., Nintendo eShop GC, Walmart GC)
  // Uses KEY-BASED comparison for deterministic "distinct" checks
  // ==========================================================================
  
  // CR099: Use new semantic fields with backwards compatibility
  const promoMerchantName = deal.promo_merchant_name || deal.merchant;
  const promoMerchantKey = deal.promo_merchant_key || toMerchantKey(promoMerchantName);
  const rewardBrandName = deal.reward_brand_name || deal.bonus_merchant || null;
  const rewardBrandKey = deal.reward_brand_key || (rewardBrandName ? toMerchantKey(rewardBrandName) : null);
  const qualifyingProductName = deal.qualifying_product_name || null;
  const rewardType = deal.reward_type || null;
  
  // CR099 Point 1: Use KEY-BASED comparison for "distinct" check (deterministic)
  // This avoids flaky string comparisons (case, punctuation, "Canada" suffix, etc.)
  const hasDistinctRewardBrand = shouldShowRewardChip(
    promoMerchantKey,
    promoMerchantName,
    rewardBrandKey,
    rewardBrandName
  );
  
  // CR099 Point 5: Only show reward chip if reward_type is meaningful
  // For non-gift_card rewards, only show if reward brand is a known program name
  const shouldShowReward = hasDistinctRewardBrand && (
    rewardType === "gift_card" || 
    rewardType === "points" ||
    (rewardBrandName && rewardBrandName.length > 0)
  );
  
  // CR099: Build headline based on available fields
  // Priority: qualifying_product_name > purchase_gift_card_brand > promo_merchant_name
  const qualifyingDisplay = qualifyingProductName || 
    deal.purchase_gift_card_brand || 
    deal.gift_card_brand;
  
  // Legacy fallback: Get display names - use merchant name for In-Kind deals
  const rawPurchaseBrand = deal.purchase_gift_card_brand ?? deal.gift_card_brand;
  const isPlaceholder = !rawPurchaseBrand || 
    rawPurchaseBrand.toLowerCase().startsWith("select") ||
    rawPurchaseBrand.toLowerCase().startsWith("choose");
  
  // CR278: For gift card BONUS deals, header/title shows the gift card being purchased (e.g., Air Canada)
  // and the retailer (e.g., Walmart) is shown in "Buy at" pill as eligibility/purchase context
  // promoMerchant = where you buy (e.g., Walmart) - shown in "Buy at" pill
  // purchaseBrand = the gift card brand (e.g., Air Canada) - shown in header and title
  const isGiftCardBonus = dealCategory === "BONUS" && rawPurchaseBrand && !isPlaceholder;
  
  // For % back DISCOUNT deals (e.g., "Walmart Travel"), use promo merchant as header
  // For BONUS deals with partner merchant (e.g., "Fig" giving Walmart GC"), use promo merchant as header
  // For In-Kind deals, prefer promo merchant name as the primary display
  const isPercentBackDiscount = dealCategory === "DISCOUNT" && deal.discount_percent && deal.merchant !== rawPurchaseBrand;
  const isPartnerBonus = dealCategory === "BONUS" && deal.merchant && deal.merchant !== rawPurchaseBrand;
  
  // CR304: Use centralized title helper which handles malformed brand names
  // This prevents "20 Walmart" from appearing as title
  const cardDisplayTitle = getDealCardTitle(deal);
  const cardCondition = getDealCardCondition(deal);
  
  // CR229 Spec: For gift card BONUS, header shows gift card brand (Air Canada), 
  // not purchase location (Walmart). Use "Buy at" pill for purchase location.
  // CR304: Use sanitized brand name via stripNumericPrefix if needed
  const purchaseBrand = isGiftCardBonus
    ? (isProbablyBadBrand(rawPurchaseBrand) ? stripNumericPrefix(rawPurchaseBrand) : rawPurchaseBrand) || cardDisplayTitle
    : dealCategory === "IN_KIND" || isPlaceholder || isPercentBackDiscount
      ? promoMerchantName
      : (qualifyingDisplay || promoMerchantName);
  
  // CR229 Spec: "Buy at" location - where you purchase (e.g., Walmart)
  // Only relevant for gift card BONUS deals where purchase location differs from gift card brand
  // CR304: Also clean the location name
  const buyAtLocation = isGiftCardBonus 
    ? (isProbablyBadBrand(promoMerchantName) ? stripNumericPrefix(promoMerchantName) : promoMerchantName)
    : null;
    
  // CR099: For reward/bonus, use reward_brand_name if available, else legacy bonus_merchant
  // CR304: Clean reward brand name to prevent "20 Walmart" patterns
  const cleanedRewardBrand = isProbablyBadBrand(rewardBrandName) 
    ? stripNumericPrefix(rewardBrandName) 
    : rewardBrandName;
  const bonusMerchant = cleanedRewardBrand || deal.bonus_merchant || deal.merchant;
  const spendAmount = deal.spend_amount;
  const bonusAmount = deal.bonus_amount ?? deal.bonus_or_discount_amount;

  // CR099: Get logos - use promo merchant for header logo
  const purchaseLogoResult = resolveBrandLogo(purchaseBrand);
  const merchantLogoResult = resolveMerchantLogo(promoMerchantName);
  const rewardLogoResult = shouldShowReward ? resolveBrandLogo(rewardBrandName) : null;

  // CR098: Ribbon styling now comes from DB config
  const isPointsDeal = dealCategory === "POINTS";
  const isWalmartBonus = bonusMerchant?.toLowerCase().includes("walmart");
  
  // Use DB-backed ribbon class with fallback for special cases
  let ribbonBg = ribbonClass;
  if (dealCategory === "BONUS" && !sameMerchantBonus && isWalmartBonus) {
    ribbonBg = "bg-[#0071CE]"; // Walmart blue for Walmart bonus
  } else if (isPartnerBonus) {
    ribbonBg = "bg-emerald-600"; // Partner bonus green
  }
  
  // CR098: Ribbon text from DB config
  const ribbonText = registry?.ribbon_text || "DEAL";
  
  // Compute bonus display value
  const bonusDisplayValue = bonusPercentValue != null 
    ? `${bonusPercentValue}%` 
    : bonusAmount 
      ? `$${bonusAmount}` 
      : null;
  
  // CR229: Get effective points values with fallback parsing
  const effectivePoints = useMemo(() => getEffectivePointsValues({
    points_amount: deal.points_amount,
    spend_threshold_amount: (deal as any).spend_threshold_amount,
    spend_amount: deal.spend_amount,
    points_program_name: deal.points_program_name,
    headline_mechanic: deal.headline_mechanic,
  }), [deal.points_amount, (deal as any).spend_threshold_amount, deal.spend_amount, deal.points_program_name, deal.headline_mechanic]);
  
  // Points display value - CR229: Now uses effective values with fallback
  const pointsDisplayValue = effectivePoints.pointsAmount 
    ? effectivePoints.pointsAmount.toLocaleString()
    : null;
  
  // CR229: Points spend threshold display
  const pointsSpendThreshold = effectivePoints.spendThreshold;

  // CR225 Addendum: Starburst is VISIBILITY-CONTROLLED, not data-gated
  // When showStarburst=true, ALWAYS render (with fallback label if no value payload)
  // When showStarburst=false, NEVER render
  const hasValuePayload = effectiveValue > 0 && dealHasValuation;

  // CR098 Task 2: Show loading skeleton if config not yet loaded (DO NOT fall back silently)
  if (!configLoaded && configLoading) {
    return <DealCardLoadingSkeleton />;
  }

  // CR225: showDebugStrip now comes from effectiveVisibility (centralized)

  return (
    <div className="group relative w-full max-w-[720px] mx-auto" data-preview-container>
      {/* CR098: Debug strip - ONLY in admin mode AND when debug_strip is explicitly enabled
          Never shown on public site */}
      {showDebugStrip && (
        <DealCardConfigDebugStrip
          dealTypeKey={dealCategory}
          originalDealType={originalDealType}
          registry={registry}
          template={template}
          isUnknownType={isUnknownType}
          configLoaded={configLoaded}
          lastUpdatedAt={lastUpdatedAt}
        />
      )}

      {/* CR225 Addendum: Starburst is visibility-controlled (NOT data-gated)
          When shouldShowStarburst=true, always render with value OR fallback label
          When shouldShowStarburst=false, never render */}
      {shouldShowStarburst && (
        <div className="absolute -top-5 -left-7 z-30">
          <ValueStarburstBadge 
            valuePercent={hasValuePayload ? effectiveValue : null}
            size="sm" 
            isPercent={rangePercentDeal}
            dealType={dealCategory}
            forceRender={!hasValuePayload} // Force render with fallback when no payload
          />
        </div>
      )}

      {/* Container - uses dynamic styling from template override + CR130 card size 
          CR130 Hardening: Uses flex column with fixed height to prevent content growth
          CR171: Uses inline style for height when custom heights are enabled */}
      <div 
        className={cn(
          "relative overflow-hidden transition-shadow duration-200 flex flex-col",
          // CR130/CR171: Apply fixed card height (empty when using inline styles)
          cardSizeClasses,
          // Only use Tailwind classes if no styling override
          !templateOverride && "bg-white shadow-[0_10px_30px_rgba(0,0,0,0.08)] border border-black/5 hover:shadow-[0_14px_38px_rgba(0,0,0,0.10)]",
          isAdmin ? "rounded-b-3xl" : "rounded-3xl"
        )}
        style={templateOverride ? {
          ...cardHeightStyle, // CR171: Include custom height
          background: containerBgColor, // Can be color or gradient string
          boxShadow: containerShadow,
          borderWidth: `${containerBorderWidth}px`,
          borderStyle: "solid",
          borderColor: containerBorderColor,
          borderRadius: isAdmin ? `0 0 ${containerRadius} ${containerRadius}` : containerRadius,
        } : cardHeightStyle}
        onMouseEnter={(e) => {
          if (templateOverride) {
            e.currentTarget.style.boxShadow = containerHoverShadow;
          }
        }}
        onMouseLeave={(e) => {
          if (templateOverride) {
            e.currentTarget.style.boxShadow = containerShadow;
          }
        }}
      >
        {/* Top Band - Purchase Brand - uses dynamic styling + CR130 header height
            CR130: Fixed height header, flex-shrink-0 to prevent shrinking */}
        <div 
          className={cn(
            "flex items-center gap-3 px-6 pr-36 relative flex-shrink-0",
            // CR130: Use size-based header height unless templateOverride is active
            !templateOverride && headerHeightClass,
            !templateOverride && "bg-gradient-to-r from-black/90 to-black/70"
          )}
          style={templateOverride ? {
            height: headerHeightPx,
            background: `linear-gradient(to right, ${headerGradientFrom}, ${headerGradientTo})`,
          } : undefined}
        >
          {/* CR100: Conditionally render merchant logo */}
          {shouldShowMerchantLogo && (
            /* CR201: Use img for all logo types - never dangerouslySetInnerHTML */
            purchaseLogoResult.svg || purchaseLogoResult.src ? (
              <img 
                src={purchaseLogoResult.svg 
                  ? `data:image/svg+xml,${encodeURIComponent(purchaseLogoResult.svg)}`
                  : purchaseLogoResult.src!
                }
                alt={purchaseBrand ?? "Brand"} 
                className="h-10 md:h-12 object-contain max-w-[120px] flex-shrink-0"
                onError={() => setLogoError(true)}
              />
            ) : null
          )}
          
          {/* CR-MerchantName: Conditionally render in header based on placement token */}
          {showMerchantInHeader && purchaseBrand && (
            <MerchantNameBlock
              merchantName={purchaseBrand}
              style={merchantNameStyle}
              context="header"
              textColor={headerTextColor}
            />
          )}
        </div>

        {/* Points Corner Ribbon - Uses DB config AND styling.showRibbon
            CR229: Now shows spend threshold (e.g., "15,000 pts per $100") */}
        {shouldShowRibbon && dealCategory === "POINTS" && (
          <div 
            data-testid="deal-ribbon"
            className={cn(
              "absolute top-0 right-0 w-36 h-36",
              ribbonBg
            )} 
            style={{ clipPath: "polygon(100% 0, 100% 100%, 0 0)" }}
          >
            <div className="absolute top-4 right-4 text-right text-white">
              <span className="text-xs uppercase tracking-wide opacity-90 block">{ribbonText}</span>
              {pointsDisplayValue && (
                <span className="text-lg font-bold block">{pointsDisplayValue} pts</span>
              )}
              {pointsSpendThreshold && (
                <span className="text-[10px] font-medium opacity-90 block">per ${pointsSpendThreshold}</span>
              )}
              {deal.points_program_name && !pointsSpendThreshold && (
                <span className="text-[10px] font-medium opacity-90 block">{deal.points_program_name}</span>
              )}
            </div>
          </div>
        )}

        {/* DISCOUNT Corner Ribbon - Uses DB config AND styling.showRibbon */}
        {shouldShowRibbon && dealCategory === "DISCOUNT" && deal.discount_percent && (
          <div 
            data-testid="deal-ribbon"
            className={cn("absolute top-0 right-0 w-36 h-36", ribbonBg)} 
            style={{ clipPath: "polygon(100% 0, 100% 100%, 0 0)" }}
          >
            <div className="absolute top-4 right-4 text-right text-white">
              <span className="text-xs uppercase tracking-wide opacity-90 block">{ribbonText}</span>
              <span className="text-lg font-bold block">{deal.discount_percent}%</span>
              {/* Show gift card brand in ribbon when different from header merchant */}
              {rawPurchaseBrand && rawPurchaseBrand !== deal.merchant && (
                <span className="text-[10px] font-medium opacity-90 block truncate max-w-[70px]">
                  {rawPurchaseBrand} GC
                </span>
              )}
            </div>
          </div>
        )}

        {/* Bonus Corner Ribbon - Uses DB config AND styling.showRibbon
            CR229 Spec: For gift card bonus, show "BONUS $<amount>" in ribbon */}
        {shouldShowRibbon && (bonusMerchant || sameMerchantBonus) && dealCategory === "BONUS" && (
          <div 
            data-testid="deal-ribbon"
            className={cn(
              "absolute top-0 right-0 w-36 h-36",
              ribbonBg
            )} 
            style={{ clipPath: "polygon(100% 0, 100% 100%, 0 0)" }}
          >
            <div className="absolute top-4 right-4 text-right text-white">
              {/* CR229 Spec: Ribbon shows "BONUS $<amount>" when bonus amount exists */}
              <span className="text-xs uppercase tracking-wide opacity-90 block">
                {bonusAmount ? `BONUS $${bonusAmount}` : ribbonText}
              </span>
              {/* No subtext in ribbon per spec - removed gift card brand, percent, etc. */}
            </div>
          </div>
        )}

        {/* Lower Body - CR130: Apply density-based padding and gaps
            CR130 Hardening: Uses flex-1 + min-h-0 to fill remaining space without growth
            CR225 Fix: Removed duplicate overflow-hidden, card-section-clamp handles it
            Added relative z-10 to ensure body content is above decorative overlays
            CR229: data-section-order shows current order for debugging */}
        <div 
          className={cn(
            "flex flex-col gap-1 card-section-clamp relative z-10",
            bodyFlexClass,
            densitySpacing ? `p-${densitySpacing.cardPadding}` : densityClasses.padding
          )}
          data-section-order={isAdmin ? sectionOrder.slice(0, 5).map(s => s.substring(0, 3)).join('→') : undefined}
        >
          {/* CR-MerchantName: BODY_TOP placement - above chips */}
          {showMerchantBodyTop && purchaseBrand && (
            <MerchantNameBlock
              merchantName={purchaseBrand}
              style={merchantNameStyle}
              context="body"
              className="mb-1"
            />
          )}
          
          {/* Deal Type Badge + Status Badge + Availability Badge + Title Row
              CR227: Added relative z-20 to ensure chips row doesn't overlap headline */}
          <div className={cn(
            "flex items-center flex-wrap relative z-20 flex-shrink-0",
            densityClasses.badgeGap
          )}>
            <DealTypeBadge category={dealCategory} size="sm" />
            <DealStatusBadge status={dealStatus} size="sm" />
            {deal.availability_scope && (
              <AvailabilityScopeBadge 
                scope={deal.availability_scope} 
                notes={deal.availability_notes}
                showUnknown={false}
              />
            )}
            {/* CR278: For gift card BONUS deals, title shows the gift card brand (Air Canada)
                For other deals, title uses promo merchant (where you spend)
                CR130: Apply title clamping based on card size
                CR225: Now respects shouldShowMerchantName master toggle */}
            {showMerchantInChipsTitle && (
              <h3 className={cn(
                "text-base font-semibold text-foreground/90",
                titleClamp
              )}>{cardDisplayTitle}</h3>
            )}
          </div>
          
          {/* CR-MerchantName: BODY_BELOW_CHIPS placement - under chips row */}
          {showMerchantBelowChips && purchaseBrand && (
            <MerchantNameBlock
              merchantName={purchaseBrand}
              style={merchantNameStyle}
              context="body"
              className="mt-1"
            />
          )}
          
          {/* CR099/CR304: Reward Brand Chip - only shown when reward brand differs from promo merchant
              CR304: Uses cleaned brand name to prevent "20 Walmart" patterns */}
          {hasDistinctRewardBrand && cleanedRewardBrand && (
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold bg-emerald-100 text-emerald-800 border border-emerald-200">
                <Gift className="w-3 h-3" />
                Reward: {cleanedRewardBrand}
              </span>
            </div>
          )}
          
          {/* CR229/CR304: "Buy at" pill for gift card bonus deals - includes spend threshold */}
          {buyAtLocation && (
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium bg-slate-100 text-slate-700 border border-slate-200">
                <Building2 className="w-3 h-3" />
                {cardCondition || `Buy at ${buyAtLocation}`}
              </span>
            </div>
          )}
          
          {/* CR098 Task 3: Admin-only warning for unknown deal types */}
          {isAdmin && isUnknownType && (
            <div className="flex items-center gap-2 px-2 py-1 bg-amber-100 border border-amber-300 rounded text-amber-800 text-xs">
              <AlertTriangle className="w-3.5 h-3.5" />
              <span>Unknown deal_type: <code className="font-mono font-bold">{originalDealType}</code> → UNCLASSIFIED</span>
            </div>
          )}
          
          {/* CR099: Admin-only validation flags warning */}
          {isAdmin && deal.validation_flags && deal.validation_flags.length > 0 && (
            <div className="flex items-center gap-2 px-2 py-1 bg-orange-100 border border-orange-300 rounded text-orange-800 text-xs">
              <AlertTriangle className="w-3.5 h-3.5" />
              <span>Validation: {deal.validation_flags.join(", ")}</span>
            </div>
          )}
          
          {/* Headline Mechanic - CR225: uses effectiveVisibility.showHeadline 
              CR229 Fix: Proper 2-line clamp with ellipsis, no overlap with adjacent sections */}
          {showHeadline && (
            <div className="mt-1 relative z-10 flex-shrink-0" data-section="headline">
              <p className={cn(
                "text-sm md:text-base text-foreground/90 font-medium leading-snug",
                headlineClamp
              )}>
                {displayHeadline}
              </p>
            </div>
          )}
          
          {/* CR061: Subtext for BONUS % back deals */}
          {isBonusPercentBackDeal(deal) && deal.discount_percent && (
            <p className="text-xs text-muted-foreground">
              {deal.discount_percent}% back in a digital gift card
            </p>
          )}
          
          {/* Value Display - CR059: Use discount-specific formatting for DISCOUNT type */}
          {dealCategory === "DISCOUNT" && discountDisplay?.metrics ? (
            <p className="text-sm text-primary font-semibold">
              {discountDisplay.metrics}
            </p>
          ) : !isBonusPercentBackDeal(deal) && dealHasValuation && percentBack != null && (
            <p className="text-sm text-primary font-semibold">
              ≈ {Math.round(percentBack)}% back
              {computedValue != null && typeof computedValue === 'number' && (
                <span className="text-black/60 font-normal"> • ≈ ${computedValue >= 10 ? Math.round(computedValue).toLocaleString() : computedValue.toFixed(2)} value</span>
              )}
            </p>
          )}

          {/* Badges Row - CR225: uses effectiveVisibility.showPromoBadges */}
          {showPromoBadges && (
            <div className="flex flex-wrap gap-2">
              {/* CR014: Membership requirement badge */}
              {membershipBadgeLabel && (
                <span className="inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-200">
                  <Lock className="w-3 h-3" />
                  {membershipBadgeLabel}
                </span>
              )}
              
              {/* CR011: Corporate badge - visible to all users */}
              {isCorporateDeal && (
                <span className="inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold bg-purple-100 text-purple-800 border border-purple-200">
                  <Building2 className="w-3 h-3" />
                  Corporate
                </span>
              )}
              
              {/* CR015: Unclassified badge */}
              {isUnclassifiedDeal && (
                <span className="inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold bg-slate-100 text-slate-800 border border-slate-200">
                  <HelpCircle className="w-3 h-3" />
                  Unclassified
                </span>
              )}
              
              {bulkDeal && (
                <span className="inline-flex rounded-full px-3 py-1 text-xs font-semibold bg-black/5 text-black/80">
                  Bulk deal
                </span>
              )}
              
              {isMissingDetails && (
                <span className="inline-flex rounded-full px-3 py-1 text-xs font-medium bg-black/5 text-black/70">
                  Details vary
                </span>
              )}
              
              {showConfirmOnSite && (
                <span className="inline-flex rounded-full px-3 py-1 text-xs font-medium bg-amber-100 text-amber-800">
                  Confirm on site
                </span>
              )}
            </div>
          )}
          
          {/* CR014: Membership requirement detail line */}
          {deal.access_requirement_notes && (
            <p className="text-xs text-amber-700 flex items-center gap-1">
              <UserCheck className="w-3 h-3" />
              {deal.access_requirement_notes}
              {deal.access_requirement_program_name && (
                <span className="font-medium">({deal.access_requirement_program_name})</span>
              )}
            </p>
          )}

          {/* Discount Tiers (for range-based deals) - CR225: uses effectiveVisibility.showDetailsPills */}
          {showDetailsPills && discountTiers.length > 0 && (
            <div className="mt-2 space-y-1 text-sm text-black/70 bg-black/5 rounded-lg p-3">
              {discountTiers.map((tier, idx) => (
                <p key={idx}>{tier}</p>
              ))}
              {bulkDeal && <p className="text-xs font-medium text-black/60 mt-1">Bulk deal</p>}
            </div>
          )}

          {/* Meta Block - CR225: uses effectiveVisibility.showDates */}
          {showDates && (
            <div className="mt-3 space-y-1 text-sm text-black/70">
              <p>{validity}</p>
              <p>{whereFound}</p>
              {bonusValidity && (
                <p className="text-teal-600 font-medium">{bonusValidity}</p>
              )}
            </div>
          )}

          {/* CR-MerchantName: BODY_ABOVE_DETAILS placement - before details section */}
          {showMerchantAboveDetails && purchaseBrand && (
            <MerchantNameBlock
              merchantName={purchaseBrand}
              style={merchantNameStyle}
              context="body"
              className="mt-2"
            />
          )}

          {/* Inline Details Section - Always visible */}
          <div className="pt-3 border-t border-black/5 mt-2">
            <DealDetailLayout deal={deal} />
            
            {/* Missing Dates Warning */}
            {isMissingDates && deal.confidence === "Low" && (
              <p className="text-xs text-black/50 mt-3">
                Validity dates not visible — check proof link.
              </p>
            )}

            {/* Unverified Warning */}
            {deal.confidence === "Low" && (
              <p className="text-xs text-black/50 mt-2">Unverified details</p>
            )}

            {/* Claim Block */}
            {deal.claim_url && (
              <div className="mt-3 space-y-1 text-sm">
                {deal.requires_physical_card && (
                  <p className="text-black/80">Purchased at: {bonusMerchant} (physical card required)</p>
                )}
                <p>
                  Claim at{" "}
                  <a 
                    href={deal.claim_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-[#0B57D0] hover:underline underline-offset-4"
                  >
                    {new URL(deal.claim_url).hostname}
                  </a>
                </p>
              </div>
            )}

            {/* Fine Print - show only first 2 lines */}
            {deal.fine_print && (
              <p className="text-xs text-black/50 mt-3 line-clamp-2">
                {deal.fine_print}
              </p>
            )}
          </div>

          {/* Actions - CR225: uses effectiveVisibility.showSourceButtons */}
          {effectiveVisibility.showSourceButtons && (
            <div className="mt-4 flex flex-wrap gap-2">
              {/* Primary action: View deal (proof URL) */}
              {deal.proof_urls?.[0] && (
                <a
                  href={deal.proof_urls[0]}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded-full px-4 py-2 text-sm font-medium bg-black text-white hover:bg-black/90 transition-colors inline-flex items-center gap-1.5"
                >
                  View deal
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
              )}
              
              {/* CR064: T&Cs button - uses terms_url if available, falls back to source_url */}
              {/* Only show if we have a terms URL and no claim_url (mutual exclusivity) */}
              {!deal.claim_url && (deal.terms_url || deal.flyer_deeplink_url || deal.source_url) && (
                <a
                  href={deal.terms_url || deal.flyer_deeplink_url || deal.source_url || "#"}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded-full px-4 py-2 text-sm font-medium bg-black/5 text-black hover:bg-black/10 transition-colors inline-flex items-center gap-1.5"
                >
                  <FileText className="w-3.5 h-3.5" />
                  {deal.terms_url ? "T&Cs" : "Source"}
                </a>
              )}
            </div>
          )}
          
          {/* CR229: Claim deal button - SEPARATE from source buttons with its own visibility gate
              TRUE HIDE: If showClaimButton is false, this never renders (no placeholder) */}
          {effectiveVisibility.showClaimButton && deal.claim_url && (
            <div className="mt-4 flex flex-wrap gap-2" data-section="claim_button">
              <a
                href={deal.claim_url}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-full px-4 py-2 text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-colors inline-flex items-center gap-1.5"
              >
                Claim deal
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </div>
          )}

          {/* CR-MerchantName: BODY_FOOTER placement - near bottom */}
          {showMerchantFooter && purchaseBrand && (
            <MerchantNameBlock
              merchantName={purchaseBrand}
              style={merchantNameStyle}
              context="body"
              className="mt-2"
            />
          )}

          {/* Footer: Confidence + Category + Flags count (admin-only, hide when 0) */}
          {/* CR105: Use normalized confidence label, never show raw numeric values */}
          <div className="flex items-center justify-between pt-3 border-t border-black/5 mt-4">
            <div className="flex items-center gap-3">
              <div className={cn("flex items-center gap-1.5 text-sm", confidenceDisplay.color)}>
                <ConfidenceIcon className="w-4 h-4" />
                <span className="font-medium">{confidenceDisplay.normalized}</span>
              </div>
              {/* CR100: Admin-only flags count - hide when 0 */}
              {isAdmin && (deal.validation_flags?.length ?? 0) > 0 && (
                <div className="text-xs text-muted-foreground">
                  {deal.validation_flags?.length} flags
                </div>
              )}
            </div>
            {categoryLabel && (
              <span className="text-xs bg-black/5 text-black/70 px-2 py-0.5 rounded-full">
                {categoryLabel}
              </span>
            )}
          </div>

          {/* Admin-only: Why this is a deal panel - CR225: uses effectiveVisibility.showWhyDealAccordion */}
          {isAdmin && effectiveVisibility.showWhyDealAccordion && (deal.evidence_snippet || deal.decision_reasons?.length || deal.proof_urls?.length) && (
            <Collapsible className="mt-3">
              <CollapsibleTrigger className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors group w-full">
                <Info className="w-3.5 h-3.5" />
                <span>Why this is a deal</span>
                <ChevronDown className="w-3 h-3 ml-auto transition-transform group-data-[state=open]:rotate-180" />
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-2 space-y-2 text-xs bg-muted/50 rounded-lg p-3 border border-border">
                {/* CR059: Debug Panel - Headline Mode & Computation */}
                {dealCategory === "DISCOUNT" && (
                  <div className="mb-2 pb-2 border-b border-border/50">
                    <span className="font-semibold text-foreground">Debug Info:</span>
                    <div className="mt-1 grid grid-cols-2 gap-1 text-muted-foreground">
                      <span>deal_type:</span>
                      <span className="font-mono">{deal.deal_type}</span>
                      <span>headline_mode:</span>
                      <span className="font-mono text-primary">{headlineMode}</span>
                      <span>audience_hint:</span>
                      <span className="font-mono">{deal.audience_hint || "none"}</span>
                      <span>qualifying_spend_cad:</span>
                      <span className="font-mono">{deal.qualifying_spend_cad ? `$${deal.qualifying_spend_cad.toLocaleString()}` : "null"}</span>
                      <span>discount_percent:</span>
                      <span className="font-mono">{deal.discount_percent ? `${deal.discount_percent}%` : "null"}</span>
                      <span>computed_value_cad:</span>
                      <span className="font-mono">{computedValue ? `$${computedValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "null"}</span>
                      <span>headline_mechanic:</span>
                      <span className="font-mono truncate" title={displayHeadline}>{displayHeadline.substring(0, 30)}...</span>
                    </div>
                  </div>
                )}
                
                {/* Evidence Snippet */}
                {deal.evidence_snippet && (
                  <div>
                    <span className="font-semibold text-foreground">Evidence:</span>
                    <p className="text-muted-foreground mt-0.5 whitespace-pre-wrap line-clamp-4">
                      {deal.evidence_snippet}
                    </p>
                  </div>
                )}
                
                {/* Decision Reasons */}
                {deal.decision_reasons && deal.decision_reasons.length > 0 && (
                  <div>
                    <span className="font-semibold text-foreground">Reasons:</span>
                    <ul className="mt-0.5 space-y-0.5 text-muted-foreground list-disc list-inside">
                      {deal.decision_reasons.map((reason, idx) => (
                        <li key={idx}>{reason}</li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {/* Source URLs */}
                {deal.proof_urls && deal.proof_urls.length > 0 && (
                  <div>
                    <span className="font-semibold text-foreground">Sources:</span>
                    <ul className="mt-0.5 space-y-0.5">
                      {deal.proof_urls.map((url, idx) => (
                        <li key={idx}>
                          <a 
                            href={url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-primary hover:underline truncate block max-w-full"
                          >
                            {url}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </CollapsibleContent>
            </Collapsible>
          )}
        </div>
      </div>
      
      {/* CR305: Debug render signature strip (safe iframe detection) */}
      {shouldRenderDealCardSignatureStrip() && (
        <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white text-[10px] px-2 py-0.5 font-mono pointer-events-none z-50">
          {buildDealCardSignature("v1", deal)}
        </div>
      )}
    </div>
  );
}
